import React, { useState } from 'react';

import { useIntl } from 'react-intl';

import { useSelector } from 'react-redux';

import { useIsMutating } from '@tanstack/react-query';

import { Dropdown, Menu } from 'antd';
import { FolderOutlined, DownloadOutlined } from '@ant-design/icons';

import RibbonButton from 'common/components/button/RibbonButton';
import CopyFolder from '../../controls/folders/CopyFolder';
import MoveFolder from '../../controls/folders/MoveFolder';
import BulkEditProduct from '../../controls/product/BulkEditProduct';
import CopyFolderModal from '../../controls/folders/CopyFolderModal';
import MoveFolderModal from '../../controls/folders/MoveFolderModal';
import EditFolderDetail from '../../controls/folders/EditFolderDetail';
import DeleteFolderDetail from '../../controls/folders/DeleteFolderDetail';
import CreateFolderDetail from '../../controls/folders/CreateFolderDetail';
import ExportProductPropertiesModal from '../../modal/bulk-edit-product/ExportProductPropertiesModal';
import ImportProductBulkEditModal from '../../modal/bulk-edit-product/ImportProductBulkEditModal';
import CreateFolderModal from '../../controls/folders/CreateFolderModal';
import FolderEditModal from 'pages/folders/components/modal/FolderEditModal';

// redux
import gridSelector from 'common/components/grid-view/controllers/selectors';
import { useInjectReducer } from 'utils/common/injectedReducers';
import { useInjectSaga } from 'utils/common/injectSaga';
import reducer from 'common/components/grid-view/controllers/reducer';
import saga from 'common/components/grid-view/controllers/saga';

import {
  useCheckPermissionAnd,
  useCheckPermissions,
} from 'hooks/useCheckPermissions';
import {
  usePermissionEditSharedProduct,
  useCheckIsSuperAdmin,
  useGlobalModal,
} from 'hooks';

import { SECURE_CONFIG } from 'utils/SecureRoute';

import Messages from 'i18n/messages/home';
import messagesProduct from 'i18n/messages/product';

import {
  ABILITY_ACTION,
  ABILITY_SUBJECT,
  PERMISSION_EDIT_PRODUCTS,
} from 'static/Permission';

import './FolderSection.less';

import { Can } from 'context/Can';
import {
  EXPORT_PRODUCT_PROPERTIES,
  useExportProductProperty,
} from '../../modal/bulk-edit-product/hook';
import { IMPORT_PRODUCT_FOR_BULK_EDITING } from 'services/product/endpoints';

const key = 'gridView';
const { SubMenu } = Menu;

const FolderDetailSection = (props) => {
  useInjectReducer({ key, reducer });
  useInjectSaga({ key, saga });

  const {
    folderDetail,
    handleLoading,
    onReloadWhenSaveSuccess,
    onMoveFolder,
    onCopyFolder,
  } = props;

  const [copyFolderModalVisible, setCopyFolderVisible] = useState(false);
  const [moveFolderModalVisible, setMoveFolderVisible] = useState(false);

  const createFolderModalVisibleHook = useState(false);
  const [, setVisibleModal] = createFolderModalVisibleHook;

  const intl = useIntl();

  let detailItems = useSelector(
    gridSelector.makeSelectDetailCurrentITemsSelection()
  );

  const exportProductPropertyMutation = useExportProductProperty();

  const checkItemsProduct = () => {
    return detailItems
      .filter((item) => item?.type?.toLowerCase() === 'product')
      .map((item) => item?.id);
  };

  const { MODAL_NAMES, checkModalVisible, closeModal } = useGlobalModal();

  const isExportProductPropertyLoading = useIsMutating({
    mutationKey: EXPORT_PRODUCT_PROPERTIES,
  });

  const isExportProductModalOpen = checkModalVisible(
    MODAL_NAMES.EXPORT_PROPERTY_MODAL
  );

  const isImportProductModalOpen = checkModalVisible(
    MODAL_NAMES.IMPORT_PROPERTY_MODAL
  );

  const isEditFolderModalOpen = checkModalVisible(MODAL_NAMES.EDIT_FOLDER);

  const detailItemsProduct = checkItemsProduct();

  const hasPermissionEditProducts = useCheckPermissions(
    PERMISSION_EDIT_PRODUCTS
  );

  const hasPermissionEditSharedProduct = usePermissionEditSharedProduct();
  const isSuperAdmin = useCheckIsSuperAdmin();

  const isAllowActionsPim =
    isSuperAdmin || hasPermissionEditProducts || hasPermissionEditSharedProduct;

  const isValidPermissionAND = useCheckPermissionAnd();

  const hasPermissionAddEditProductOverlay = isValidPermissionAND([
    SECURE_CONFIG.OVERLAY_CREATE.PASS,
    SECURE_CONFIG.OVERLAY_EDIT.PASS,
  ]);

  const closeCopyModal = () => {
    setCopyFolderVisible(false);
  };

  const closeMoveModal = () => {
    setMoveFolderVisible(false);
  };

  const cancelEditFolder = () => {
    closeModal(MODAL_NAMES.EDIT_FOLDER);
  };

  const saveEditFolderSuccess = () => {
    cancelEditFolder();
    onReloadWhenSaveSuccess && onReloadWhenSaveSuccess(folderDetail.id, 'edit');
  };

  const onExportProductProperty = (exportProperties, allPopulated) => {
    exportProductPropertyMutation.mutate({
      productIds: detailItemsProduct,
      folderIds: [folderDetail?.id],
      selectedColumns: exportProperties,
      allPopulated,
    });
  };

  const menuOptions = (
    <Menu className='folder-section__menu'>
      <Menu.Item key='createFolder' style={{ backgroundColor: 'inherit' }}>
        <Can I={ABILITY_ACTION.CREATE} a={ABILITY_SUBJECT.FOLDER} passThrough>
          {(allowed) => (
            <CreateFolderDetail
              setVisibleModal={setVisibleModal}
              disabled={!allowed || !folderDetail.editable}
            />
          )}
        </Can>
      </Menu.Item>
      <Menu.Item key='editFolder' style={{ backgroundColor: 'inherit' }}>
        <Can I={ABILITY_ACTION.EDIT} a={ABILITY_SUBJECT.FOLDER} passThrough>
          {(allowed) => (
            <EditFolderDetail
              disabled={!allowed || !folderDetail.editable}
              selectedFolders={folderDetail}
              handleLoading={handleLoading}
              onReloadWhenSaveSuccess={onReloadWhenSaveSuccess}
            />
          )}
        </Can>
      </Menu.Item>
      <Menu.Item key='deleteFolder' style={{ backgroundColor: 'inherit' }}>
        <Can I={ABILITY_ACTION.DELETE} a={ABILITY_SUBJECT.FOLDER} passThrough>
          {(allowed) => (
            <DeleteFolderDetail
              disabled={!allowed || !folderDetail.editable}
              selectedFolders={folderDetail}
              handleLoading={handleLoading}
              onReloadWhenSaveSuccess={onReloadWhenSaveSuccess}
            />
          )}
        </Can>
      </Menu.Item>
      <Menu.Item key='copyFolder' style={{ backgroundColor: 'inherit' }}>
        <Can I={ABILITY_ACTION.COPY} a={ABILITY_SUBJECT.FOLDER} passThrough>
          {(allowed) => (
            <CopyFolder
              disabled={!allowed}
              selectedFolders={[folderDetail]}
              onClick={() => setCopyFolderVisible(true)}
            />
          )}
        </Can>
      </Menu.Item>
      <Menu.Item key='moveFolder' style={{ backgroundColor: 'inherit' }}>
        <Can I={ABILITY_ACTION.COPY} a={ABILITY_SUBJECT.FOLDER} passThrough>
          {(allowed) => (
            <MoveFolder
              disabled={!allowed || !folderDetail.editable}
              selectedFolders={[folderDetail]}
              onClick={() => setMoveFolderVisible(true)}
            />
          )}
        </Can>
      </Menu.Item>

      <SubMenu
        key={'bulk-edit-product'}
        style={{ backgroundColor: 'inherit' }}
        disabled={!isAllowActionsPim}
        title={
          <RibbonButton
            size='small'
            className='button-item-dropdown'
            icon={<DownloadOutlined />}
            disabled={!isAllowActionsPim}
            label={messagesProduct.bulkEditProduct}
          />
        }
      >
        <BulkEditProduct
          disabled={!isAllowActionsPim}
          disabledExport={!isAllowActionsPim}
          selectedProducts={detailItemsProduct}
          selectedFolders={[folderDetail?.id]}
        />
      </SubMenu>
    </Menu>
  );
  return (
    <>
      <Dropdown overlay={menuOptions} placement='bottomLeft' arrow>
        <RibbonButton
          icon={<FolderOutlined />}
          label={Messages.folderActions}
        />
      </Dropdown>
      <CopyFolderModal
        visible={copyFolderModalVisible}
        closeModal={closeCopyModal}
        selectedFolder={folderDetail}
        onCopy={onCopyFolder}
      />
      <MoveFolderModal
        visible={moveFolderModalVisible}
        currentFolderId={folderDetail?.id}
        closeModal={closeMoveModal}
        selectedItems={[folderDetail]}
        onMove={onMoveFolder}
      />
      {isExportProductModalOpen && (
        <ExportProductPropertiesModal
          title={intl.formatMessage(messagesProduct.exportProperties)}
          onExport={onExportProductProperty}
          visible={isExportProductModalOpen}
          loading={isExportProductPropertyLoading}
          closeModal={() => closeModal(MODAL_NAMES.EXPORT_PROPERTY_MODAL)}
        />
      )}
      {isImportProductModalOpen && (
        <ImportProductBulkEditModal
          title={intl.formatMessage(messagesProduct.importProductBulkEditTitle)}
          apiUrl={IMPORT_PRODUCT_FOR_BULK_EDITING}
          visible={isImportProductModalOpen}
          closeModal={() => closeModal(MODAL_NAMES.IMPORT_PROPERTY_MODAL)}
        />
      )}
      <CreateFolderModal
        visibleHook={createFolderModalVisibleHook}
        onReloadWhenSaveSuccess={onReloadWhenSaveSuccess}
        parentId={folderDetail?.id}
        skipNotification
      />
      {isEditFolderModalOpen ? (
        <FolderEditModal
          visible={isEditFolderModalOpen}
          onCancel={cancelEditFolder}
          onSaveSuccess={saveEditFolderSuccess}
          folderInfo={folderDetail}
          idSelected={folderDetail?.id}
        />
      ) : null}
    </>
  );
};

export default FolderDetailSection;
